import { confirmJobseekerEdit } from "../lib/jobseekerEdit";
import { useEffect, useState, useRef } from "react";
import { useRoute, useLocation } from "wouter";
import { FileText, Download, Upload, Star, Trash2, Loader2, CheckCircle2 } from "lucide-react";
import { messagesHref } from "../lib/actions";
import { DetailLoading, DetailNotFound } from "../components/DetailStates";
import { api, apiAssetUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSafeSearch } from "../lib/router-safe";
import ProfileVariantA from "../components/ProfileVariantA";

export default function Profile() {
  const [, params] = useRoute("/u/:id");
  const id = Number(params?.id);
  const [, navigate] = useLocation();
  const [hireOpen, setHireOpen] = useState(false);
  const { user: authUser, loading: authLoading } = useAuth();
  const search = useSafeSearch();
  // A public preview deliberately hides the signed-in owner's editing controls.
  const isPublicPreview = new URLSearchParams(search).get("view") === "public";
  const isSelf = !!authUser && authUser.id === id && !isPublicPreview;
  const isOwnPublicPreview = !!authUser && authUser.id === id && isPublicPreview;
  const [data, setData] = useState<any>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound">("loading");
  const [portfolio, setPortfolio] = useState<any[]>([]); // [128]
  const [bust, setBust] = useState(0); // cache-bust images after upload
  const [uploading, setUploading] = useState<null | "avatar" | "banner">(null);
  const [removingBanner, setRemovingBanner] = useState(false);
  const [isBannerMenuOpen, setIsBannerMenuOpen] = useState(false);
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const [isAvatarPreviewOpen, setIsAvatarPreviewOpen] = useState(false);
  const [resumeDownloadBusy, setResumeDownloadBusy] = useState(false);
  const [resumeDownloadError, setResumeDownloadError] = useState<string | null>(null);
  const [messageEligibility, setMessageEligibility] = useState<{ key: string; canMessage: boolean } | null>(null);
  const avatarInput = useRef<HTMLInputElement>(null);
  const bannerInput = useRef<HTMLInputElement>(null);
  const bannerMenuRef = useRef<HTMLDivElement>(null);
  const avatarMenuRef = useRef<HTMLDivElement>(null);

  function reload() {
    api.getProfile(id).then((d) => { if (d?.user) setData(d); }).catch(() => {});
  }
  async function onPickImage(kind: "avatar" | "banner", file?: File | null) {
    if (!file || uploading) return;
    setUploading(kind);
    try { await api.uploadProfileImage(kind, file); setBust(Date.now()); reload(); }
    catch (error) { throw error; }
    finally { setUploading(null); }
  }
  async function removeBanner() {
    if (!bannerSrc || uploading || !window.confirm("Remove your profile banner? This cannot be undone.")) return;
    setRemovingBanner(true);
    setUploading("banner");
    try { await api.removeProfileBanner(); setBust(Date.now()); reload(); }
    catch { window.alert("Could not remove the banner. Please try again."); }
    finally { setUploading(null); setRemovingBanner(false); }
  }

  async function downloadResume(attachmentId: string) {
    setResumeDownloadBusy(true);
    setResumeDownloadError(null);
    try { await api.downloadResumeAttachment(id, attachmentId); }
    catch (e: unknown) { setResumeDownloadError(e instanceof Error ? e.message : "Could not download the résumé file."); }
    finally { setResumeDownloadBusy(false); }
  }

  function goBack() {
    navigate("/marketplace?tab=Jobseekers");
  }

  useEffect(() => {
    let alive = true;
    setState("loading");
    setHireOpen(false);
    api.getProfile(id)
      .then((d) => { if (!alive) return; if (d?.user) { setData(d); setState("ready"); } else setState("notfound"); })
      .catch(() => { if (alive) setState("notfound"); });
    return () => { alive = false; };
  }, [id]);

  useEffect(() => {
    function closeBannerMenu(event: MouseEvent) {
      if (event.target instanceof Node && !bannerMenuRef.current?.contains(event.target)) setIsBannerMenuOpen(false);
    }
    document.addEventListener("mousedown", closeBannerMenu);
    return () => document.removeEventListener("mousedown", closeBannerMenu);
  }, []);

  useEffect(() => {
    function closeAvatarMenu(event: MouseEvent) {
      if (event.target instanceof Node && !avatarMenuRef.current?.contains(event.target)) setIsAvatarMenuOpen(false);
    }
    document.addEventListener("mousedown", closeAvatarMenu);
    return () => document.removeEventListener("mousedown", closeAvatarMenu);
  }, []);

  useEffect(() => {
    let alive = true;
    if (Number.isFinite(id)) api.workerMedia(id).then((m) => { if (alive) setPortfolio(m); }).catch(() => { if (alive) setPortfolio([]); });
    return () => { alive = false; };
  }, [id]);

  // Keep Message disabled until this viewer/profile pair qualifies. A failed lookup
  // also leaves it disabled; a listing remains the route for first contact.
  useEffect(() => {
    if (!authUser || !Number.isInteger(id) || authUser.id === id) return;
    let alive = true;
    const key = `${authUser.id}:${id}`;
    api.directMessageEligibility(id)
      .then(({ canMessage }) => { if (alive) setMessageEligibility({ key, canMessage }); })
      .catch(() => { if (alive) setMessageEligibility({ key, canMessage: false }); });
    return () => { alive = false; };
  }, [authUser?.id, id]);

  if (state === "loading") return <DetailLoading label="Loading profile…" />;
  if (state === "notfound" || !data) return <DetailNotFound title="Profile not found" />;

  const { user } = data;
  // Owner responses may include a private résumé, including in public-preview mode.
  const isOtherMember = !authLoading && authUser?.id !== id;
  const showJobseekerActions = isOtherMember && Boolean(data.activeResume);
  const messageHref = messagesHref(id, { type: "direct", name: user.name });
  const canMessage = Boolean(authUser && messageEligibility?.key === `${authUser.id}:${id}` && messageEligibility.canMessage);

  const avatarBase = apiAssetUrl(user.profileImageUrl);
  const bannerBase = apiAssetUrl(user.bannerImageUrl);
  const bustQ = bust ? `${bannerBase?.includes("?") ? "&" : "?"}b=${bust}` : "";
  const avatarSrc = avatarBase ? `${avatarBase}${bust ? `${avatarBase.includes("?") ? "&" : "?"}b=${bust}` : ""}` : null;
  const bannerSrc = bannerBase ? `${bannerBase}${bustQ}` : null;

  return <ProfileVariantA
    data={{ ...data, portfolio }}
    onBack={goBack}
    isOwnPublicPreview={isOwnPublicPreview}
    isSelf={isSelf}
    isOtherMember={isOtherMember}
    showJobseekerActions={showJobseekerActions}
    canMessage={canMessage}
    messageHref={messageHref}
    avatarSrc={avatarSrc}
    bannerSrc={bannerSrc}
    uploading={uploading}
    removingBanner={removingBanner}
    isBannerMenuOpen={isBannerMenuOpen}
    setIsBannerMenuOpen={setIsBannerMenuOpen}
    bannerInput={bannerInput}
    bannerMenuRef={bannerMenuRef}
    onPickImage={onPickImage}
    removeBanner={removeBanner}
    isAvatarMenuOpen={isAvatarMenuOpen}
    setIsAvatarMenuOpen={setIsAvatarMenuOpen}
    avatarInput={avatarInput}
    avatarMenuRef={avatarMenuRef}
    isAvatarPreviewOpen={isAvatarPreviewOpen}
    setIsAvatarPreviewOpen={setIsAvatarPreviewOpen}
    resumeDownloadBusy={resumeDownloadBusy}
    resumeDownloadError={resumeDownloadError}
    downloadResume={downloadResume}
    onOpenHire={() => setHireOpen(true)}
    hireOpen={hireOpen}
    setHireOpen={setHireOpen}
    resumeManagement={isSelf ? <ResumeAttachments ownerUserId={id} /> : null}
  />;

}

// v3.9.40 (CEO #7) — one résumé, multiple uploaded attachments, exactly one active.
function ResumeAttachments({ ownerUserId }: { ownerUserId: number }) {
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  async function load() { const d = await api.myResumes().catch(() => null); setRows(d?.attachments || []); }
  useEffect(() => { load(); }, []);

  async function upload(file?: File | null) {
    if (!file) return;
    setBusy(true); setErr(null);
    try { await api.uploadResume(file); await load(); }
    catch (e: any) { setErr(e?.message || "Could not upload the file."); }
    finally { setBusy(false); }
  }
  async function makeActive(id: string) {
    setBusy(true); setErr(null);
    try {
      const saved = await confirmJobseekerEdit((confirmed) => api.setActiveResume(id, confirmed));
      if (saved) { await load(); }
    } catch (error: unknown) { setErr(error instanceof Error ? error.message : "Could not update the résumé."); }
    finally { setBusy(false); }
  }
  async function remove(id: string) {
    setBusy(true); setErr(null);
    try {
      const saved = await confirmJobseekerEdit((confirmed) => api.deleteResume(id, confirmed));
      if (saved) { await load(); }
    } catch (error: unknown) { setErr(error instanceof Error ? error.message : "Could not update the résumé."); }
    finally { setBusy(false); }
  }
  async function download(id: string) {
    setBusy(true);
    setErr(null);
    try { await api.downloadResumeAttachment(ownerUserId, id); }
    catch (e: unknown) { setErr(e instanceof Error ? e.message : "Could not download the résumé file."); }
    finally { setBusy(false); }
  }

  const fmtSize = (n?: number) => { const b = Number(n) || 0; return b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1048576).toFixed(1)} MB`; };

  return (
    <div className="card p-5 mb-6">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h2 className="font-bold text-navy flex items-center gap-2"><FileText className="w-4 h-4" />My résumé files</h2>
        <input ref={fileInput} type="file" accept=".pdf,.doc,.docx,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden"
          onChange={(e) => { upload(e.target.files?.[0]); e.currentTarget.value = ""; }} />
        <button onClick={() => fileInput.current?.click()} disabled={busy} className="btn-red h-9 px-3 text-sm disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Upload
        </button>
      </div>
      <p className="text-xs text-mut mb-3">You have one résumé profile, but you can keep several files (PDF, Word, or image) and choose which one is <b>active</b> — the active file is what clients download.</p>
      {err && <p className="text-sm text-brand-red mb-2">{err}</p>}
      {rows === null ? <p className="text-sm text-mut">Loading…</p>
        : rows.length === 0 ? <p className="text-sm text-mut">No files yet. Upload your résumé to let clients download it.</p>
        : (
          <div className="space-y-2">
            {rows.map((a) => (
              <div key={a.id} className={`flex items-center gap-3 rounded-lg border p-3 ${a.active ? "border-brand-green/50 bg-brand-green/5" : "border-line"}`}>
                <FileText className="w-5 h-5 text-navy shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-navy truncate">{a.fileName}</div>
                  <div className="text-[11px] text-mut">{fmtSize(a.sizeBytes)}{a.active && <span className="text-brand-greendark font-semibold"> · Active</span>}</div>
                </div>
                  <button onClick={() => void download(a.id)} disabled={busy} className="btn-ghost h-8 w-8 p-0 disabled:opacity-60" title="View / download"><Download className="w-4 h-4" /></button>
                {a.active
                  ? <span className="chip bg-brand-green/15 text-brand-greendark flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Active</span>
                  : <button onClick={() => makeActive(a.id)} className="btn-outline h-8 px-2 text-xs flex items-center gap-1"><Star className="w-3 h-3" />Set active</button>}
                <button onClick={() => remove(a.id)} className="btn-ghost h-8 w-8 p-0 text-brand-red" title="Delete"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
